import React, { useCallback, useEffect, useState } from 'react';
import { jobTypesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLocalization } from '../context/LocalizationContext';
import styles from '../styles/JobTypesPage.module.css';

const JobTypesPage = () => {
  const { can } = useAuth();
  const { t } = useLocalization();
  const [jobTypes, setJobTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    durationHours: '',
  });

  const fetchJobTypes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await jobTypesAPI.getAll();
      setJobTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch job types:', error);
      alert(t('job_types_load_failed', 'Failed to load job types'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchJobTypes();
  }, [fetchJobTypes]);

  const handleOpenModal = (jobType = null) => {
    if (jobType) {
      setEditingId(jobType.id);
      setFormData({
        name: jobType.name,
        description: jobType.description || '',
        durationHours: jobType.duration_hours || '',
      });
    } else {
      setEditingId(null);
      setFormData({
        name: '',
        description: '',
        durationHours: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({
      name: '',
      description: '',
      durationHours: '',
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        durationHours: formData.durationHours ? Number(formData.durationHours) : null,
      };

      if (editingId) {
        await jobTypesAPI.update(editingId, payload);
        alert(t('job_types_update_success', 'Job type updated successfully'));
      } else {
        await jobTypesAPI.create(payload);
        alert(t('job_types_create_success', 'Job type created successfully'));
      }

      handleCloseModal();
      fetchJobTypes();
    } catch (error) {
      console.error('Failed to save job type:', error);
      alert(error.response?.data?.message || t('job_types_save_failed', 'Failed to save job type'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('job_types_delete_confirm', 'Are you sure you want to delete this job type?'))) return;

    try {
      await jobTypesAPI.delete(id);
      alert(t('job_types_delete_success', 'Job type deleted successfully'));
      fetchJobTypes();
    } catch (error) {
      console.error('Failed to delete job type:', error);
      alert(error.response?.data?.message || t('job_types_delete_failed', 'Failed to delete job type'));
    }
  };

  if (!can('tasks', 'view')) {
    return <div className={styles.error}>{t('job_types_permission_denied', 'You do not have permission to access this page.')}</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>
          <i className="bi bi-wrench-adjustable"></i> {t('job_types_title', 'Job Types')}
        </h1>
        <p className={styles.subtitle}>{t('job_types_subtitle', 'Manage job types and their configurations')}</p>
      </div>

      {loading ? (
        <div className={styles.loading}>{t('job_types_loading', 'Loading job types...')}</div>
      ) : (
        <div className={styles.content}>
          {can('tasks', 'create') && (
            <button className={styles.createBtn} onClick={() => handleOpenModal()}>
              {t('job_types_new', '+ New Job Type')}
            </button>
          )}

          {jobTypes.length === 0 ? (
            <div className={styles.empty}>{t('job_types_empty', 'No job types found.')}</div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('localization_name', 'Name')}</th>
                    <th>{t('tasks_label_description', 'Description')}</th>
                    <th>{t('job_types_duration_hours', 'Duration (hours)')}</th>
                    <th>{t('users_actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {jobTypes.map((jobType) => (
                    <tr key={jobType.id}>
                      <td className={styles.nameCell}>{jobType.name}</td>
                      <td className={styles.descCell}>{jobType.description || '-'}</td>
                      <td className={styles.durationCell}>{jobType.duration_hours || '-'}</td>
                      <td className={styles.actionsCell}>
                        {can('tasks', 'edit') && (
                          <button
                            className={styles.editBtn}
                            onClick={() => handleOpenModal(jobType)}
                            title={t('teams_edit', 'Edit')}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                        )}
                        {can('tasks', 'delete') && (
                          <button
                            className={styles.deleteBtn}
                            onClick={() => handleDelete(jobType.id)}
                            title={t('teams_delete', 'Delete')}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h2>{editingId ? t('job_types_modal_edit', 'Edit Job Type') : t('job_types_modal_new', 'New Job Type')}</h2>
            <form onSubmit={handleSave}>
              <div className={styles.formGroup}>
                <label>{t('job_types_name_required', 'Name *')}</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('job_types_name_placeholder', 'e.g., Site Inspection')}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('tasks_label_description', 'Description')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('job_types_description_placeholder', 'Job type description')}
                  rows="3"
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('job_types_duration_hours', 'Duration (hours)')}</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={formData.durationHours}
                  onChange={(e) => setFormData({ ...formData, durationHours: e.target.value })}
                  placeholder={t('job_types_duration_placeholder', 'e.g., 4')}
                />
              </div>
              <div className={styles.modalButtons}>
                <button type="submit" className={styles.submitBtn}>
                  {editingId ? t('common_update', 'Update') : t('users_create', 'Create')}
                </button>
                <button type="button" className={styles.cancelBtn} onClick={handleCloseModal}>
                  {t('common_cancel', 'Cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobTypesPage;
